import { type Table, flexRender } from "@tanstack/react-table";
import { cn, getPinningCellClassName, getPinningStyles } from "../../lib/utils";
import { TableHead, TableHeader, TableRow } from "../table";
import SortArrows from "./sort-arrows";

type DataTableHeaderProps<T extends { id: unknown }> = {
  table: Table<T>;
  testIdPrefix?: string;
};

export const DataTableHeader = <T extends { id: unknown }>({
  table,
  testIdPrefix,
}: DataTableHeaderProps<T>) => {
  return (
    <TableHeader className="relative">
      {table.getHeaderGroups().map((headerGroup) => (
        <TableRow key={headerGroup.id}>
          {headerGroup.headers.map((header) => (
            <TableHead
              key={header.id}
              className={cn(
                getPinningCellClassName(header.column, true),
                header.column.getIsFiltered() && "text-primary",
              )}
              style={getPinningStyles(header.column, true)}
              data-testid={
                testIdPrefix ? `${testIdPrefix}-header-${header.id}` : undefined
              }
            >
              {header.isPlaceholder ? null : (
                <div className="flex items-center gap-2">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {header.column.getCanSort() && (
                    <button
                      type="button"
                      className="cursor-pointer p-1"
                      onClick={() => {
                        header.column.toggleSorting();
                      }}
                    >
                      <SortArrows order={header.column.getIsSorted()} />
                    </button>
                  )}
                </div>
              )}

              {header.column.getCanResize() && (
                <div
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  className={`absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-gray-400 ${
                    header.column.getIsResizing()
                      ? "bg-gray-500 opacity-100"
                      : "opacity-0 hover:opacity-100"
                  }`}
                />
              )}
            </TableHead>
          ))}
        </TableRow>
      ))}
    </TableHeader>
  );
};
